/**
 * The platform's fee on one client's profit, and the one way to change it.
 *
 * A change is a new immutable version that starts at the next UTC day, so
 * nothing already settled is ever billed under a rate set afterwards. It is
 * money, so the administrator's password is asked again.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/design-system/Button";
import { setPlatformClientFee } from "@/shared/api";
import type { PlatformClientResponse } from "@/shared/api";
import { fromPercent, percent } from "@/shared/money/money";

const FIELD =
  "min-h-9 rounded-row border border-border-strong bg-surface-default px-2 text-text-primary focus-visible:outline-2 focus-visible:outline-focus-ring";

export function ClientFee({ client }: { readonly client: PlatformClientResponse }): React.JSX.Element {
  const queries = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [rate, setRate] = useState("");
  const [password, setPassword] = useState("");
  const save = useMutation({
    mutationFn: async () => {
      const { response } = await setPlatformClientFee({
        path: { tenant_id: client.tenant_id, account_id: client.account_id },
        body: { rate: fromPercent(rate), current_password: password },
      });
      if (response?.ok !== true) throw new Error(response?.status === 403 ? "password" : "refused");
    },
    onSuccess: async () => {
      setEditing(false);
      setRate("");
      setPassword("");
      await queries.invalidateQueries({ queryKey: ["platform-clients"] });
    },
    onSettled: () => { setPassword(""); },
  });

  const current = client.fee_rate == null ? "не задана" : percent(client.fee_rate, 0);
  return (
    <div className="flex flex-col gap-1" data-testid="client-fee">
      <span className="flex items-center gap-2">
        <span className="text-text-primary tabular-nums">{current}</span>
        {!editing && <Button variant="ghost" size="sm" onClick={() => { setEditing(true); }}>Изменить</Button>}
      </span>
      {client.next_fee_rate != null && (
        <span className="text-helper-sm text-text-muted">с завтра {percent(client.next_fee_rate, 0)}</span>
      )}
      {editing && (
        <form className="flex flex-col gap-2" onSubmit={(event) => { event.preventDefault(); if (!save.isPending) save.mutate(); }}>
          <label className="flex flex-col gap-1">
            <span className="text-helper-sm">Комиссия с прибыли, %</span>
            <input aria-label="Комиссия с прибыли, %" className={`${FIELD} w-24`} inputMode="decimal" required value={rate} onChange={(event) => { setRate(event.target.value); }} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-helper-sm">Ваш пароль</span>
            <input aria-label="Ваш пароль" className={`${FIELD} w-40`} type="password" autoComplete="current-password" required value={password} onChange={(event) => { setPassword(event.target.value); }} />
          </label>
          <span className="text-helper-sm">Новая ставка действует со следующих суток (UTC); прошлые расчёты не меняются.</span>
          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={save.isPending || rate === "" || password === ""}>Сохранить</Button>
            <Button onClick={() => { setEditing(false); }}>Отмена</Button>
          </div>
          {save.isError && (
            <span role="alert" className="text-helper-sm text-status-warning">
              {save.error.message === "password" ? "Пароль не подошёл." : "Ставка не принята: от 0 до 100 %, не более 4 знаков доли."}
            </span>
          )}
        </form>
      )}
    </div>
  );
}
